import React from 'react'

import { SimpleTable, TableSkeleton, AssetDetailModal, type AddressCell, Alert } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { celsiusToUser, formatSpeed, type SpeedUnit } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatUserDate } from '@/utils/formatDate'

const PAGE_SIZE = 1000

interface ReeferOperationDetailParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

interface ReeferOperationDetailSample {
  id: number
  assetName: string
  sampleTime: Date
  bearingDegrees: number | null
  speedKmsHr: number | null
  powerState: string | null
  powerMode: string | null
  powerSource: string | null
  batteryVolts: number | null
  engineSpeed: string | null
  operationalMode: string | null
  defrostInterval: number | null
  airControl: string | null
  ambientTemperature: number | null
  zone1Mode: string | null
  zone1SP: number | null
  zone1SA: number | null
  zone1RA: number | null
  zone2Mode: string | null
  zone2SP: number | null
  zone2SA: number | null
  zone2RA: number | null
  zone3Mode: string | null
  zone3SP: number | null
  zone3SA: number | null
  zone3RA: number | null
  location: string | null
  latitude: number | null
  longitude: number | null
  assetId: number
  companyId: number
  umdId: number
}

const ZONES = [1, 2, 3] as const

const formatNumber = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '' : value.toFixed(digits)

const formatSpeedCell = (speedKmsHr: number | null | undefined, unit: SpeedUnit): string =>
  speedKmsHr === null || speedKmsHr === undefined || !Number.isFinite(speedKmsHr) ? '' : formatSpeed(speedKmsHr, unit)

const ReeferOperationDetail: InsightDefinition<ReeferOperationDetailParams, ReeferOperationDetailSample[]> = {
  active: true,
  slug: 'asset/reefer-operation-detail',
  category: 'asset',
  name: 'Reefer Operation Detail',
  description: 'Full reefer operational detail, one row per sample',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: ReeferOperationDetailParams) => {
      if (!configuration.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Enter the custom date range'
        }
        const from = new Date(configuration.from)
        const to = new Date(configuration.to)
        if (from > to) {
          return 'Start date must be before end date'
        }

        const within30Days = validateDateRangeWithin30Days(from.toISOString(), to.toISOString())
        if (within30Days !== true) {
          return within30Days
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: ReeferOperationDetailParams) => {
    const path = `/insights/asset/reefer-operation-detail?${buildQuery({ ...parameters, take: PAGE_SIZE })}`
    const response = await api.get<ReeferOperationDetailSample[]>({
      path,
      origin: 'ReeferOperationDetail',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferOperationDetailParams, title: string) => {
    const path = '/insights/asset/reefer-operation-detail'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const [selectedAsset, setSelectedAsset] = React.useState<number>()
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={26} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (!data || isInsightStatus(data)) {
      return null
    }
    if (!data.length) {
      return null
    }

    const parameters = configuration.parameters as ReeferOperationDetailParams
    const speedUnit = units === 'Metric' ? 'kph' : 'mph'
    type HeaderType = { key: string; title: string; size: number } | string
    const headers: HeaderType[] = [
      'Name',
      { key: 'Date', title: 'Date', size: 200 },
      'Bearing',
      'Speed',
      'Power State',
      'Power Mode',
      'Power Source',
      'Battery Volts',
      'Engine Speed',
      'Operational Mode',
      'Defrost Interval',
      'Air Control',
      'Ambient',
    ]

    ZONES.forEach((zone) => {
      headers.push(`Z${zone} Mode`, `Z${zone} SP`, `Z${zone} SA`, `Z${zone} RA`)
    })
    headers.push('Location')

    const body = data.map((sample) => {
      const row: Record<string, string | number | AddressCell | React.ReactNode> = {
        Name: sample.assetName ? (
          <button
            onClick={() => setSelectedAsset(sample.assetId)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {sample.assetName}
          </button>
        ) : (
          ''
        ),
        Date: formatUserDate(sample.sampleTime),
        Bearing: formatNumber(sample.bearingDegrees, 0),
        Speed: formatSpeedCell(sample.speedKmsHr, speedUnit),
        'Power State': sample.powerState ?? '',
        'Power Mode': sample.powerMode ?? '',
        'Power Source': sample.powerSource ?? '',
        'Battery Volts': formatNumber(sample.batteryVolts, 2),
        'Engine Speed': sample.engineSpeed ?? '',
        'Operational Mode': sample.operationalMode ?? '',
        'Defrost Interval': formatNumber(sample.defrostInterval),
        'Air Control': sample.airControl ?? '',
        Ambient: celsiusToUser(sample.ambientTemperature, 2, units),
      }

      ZONES.forEach((zone) => {
        Object.assign(row, {
          [`Z${zone} Mode`]: sample[`zone${zone}Mode`] ?? '',
          [`Z${zone} SP`]: celsiusToUser(sample[`zone${zone}SP`], 2, units),
          [`Z${zone} SA`]: celsiusToUser(sample[`zone${zone}SA`], 2, units),
          [`Z${zone} RA`]: celsiusToUser(sample[`zone${zone}RA`], 2, units),
        })
      })

      row['Location'] =
        sample.location == null && (sample.latitude == null || sample.longitude == null)
          ? ''
          : genericLocationDrilldown({
              configuration,
              address: sample.location ?? undefined,
              latitude: sample.latitude ?? undefined,
              longitude: sample.longitude ?? undefined,
              companyId: parameters.companyId,
              markerFields: {
                Asset: sample.assetName ?? '',
                Time: formatUserDate(sample.sampleTime) ?? '',
              },
            })
      return row
    })

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as ReeferOperationDetailParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/fresh/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false`,
            origin: 'ReeferOperationDetail#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    if (!data || isInsightStatus(data)) {
      return ''
    }
    const rows = data.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferOperationDetail
